import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { LearningPattern } from '@/lib/enterprise/learningWeights';
import type { RecurrenceEntry } from '@/lib/enterprise/compositePatterns';
import { classifyOutcomes, type OutcomePattern, type RawOutcomeRow } from '@/lib/enterprise/outcomePatterns';

export type LearningEventType =
  | 'signal_acknowledged'
  | 'signal_dismissed'
  | 'signal_view_shop'
  | 'signal_view_intelligence'
  | 'recommendation_view_shop'
  | 'recommendation_view_intelligence'
  | 'intelligence_shop_drilldown'
  | 'guided_action_viewed'
  | 'guided_action_clicked'
  | 'playbook_opened'
  | 'playbook_step_viewed'
  | 'playbook_step_clicked';

export type LearningSourceType = 'signal' | 'recommendation' | 'intelligence';

export interface LearningSummaryRow {
  event_type: string;
  source_type: string;
  event_count: number;
}

export function useNetworkLearning() {
  const [summary, setSummary] = useState<LearningSummaryRow[]>([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [patterns, setPatterns] = useState<LearningPattern[]>([]);
  const [isLoadingPatterns, setIsLoadingPatterns] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceEntry[]>([]);
  const [isLoadingRecurrence, setIsLoadingRecurrence] = useState(false);
  const [outcomes, setOutcomes] = useState<OutcomePattern[]>([]);
  const [isLoadingOutcomes, setIsLoadingOutcomes] = useState(false);

  /**
   * Fire-and-forget event recording. Does not block the UI.
   */
  const recordEvent = useCallback((
    sourceType: LearningSourceType,
    sourceId: string | null,
    eventType: LearningEventType,
    shopId?: string | null,
    metadata?: Record<string, unknown>,
  ) => {
    supabase.rpc('record_learning_event', {
      _source_type: sourceType,
      _source_id: sourceId ?? undefined,
      _event_type: eventType,
      _shop_id: shopId ?? undefined,
      _event_metadata: metadata ?? {},
    } as any).then(({ error }) => {
      if (error) console.error('[useNetworkLearning] recordEvent error:', error);
    });
  }, []);

  const loadSummary = useCallback(async (days = 30) => {
    setIsLoadingSummary(true);
    try {
      const { data, error } = await supabase.rpc('get_learning_summary', {
        _days: days,
      } as any);

      if (error) {
        console.error('[useNetworkLearning] loadSummary error:', error);
        return;
      }

      const rows = Array.isArray(data) ? data : [];
      setSummary(rows as unknown as LearningSummaryRow[]);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  /**
   * Load aggregated learning patterns for the Pensieve Activation Layer.
   */
  const loadPatterns = useCallback(async (days = 30) => {
    setIsLoadingPatterns(true);
    try {
      const { data, error } = await supabase.rpc('get_learning_patterns', {
        _days: days,
      } as any);

      if (error) {
        console.error('[useNetworkLearning] loadPatterns error:', error);
        return;
      }

      const parsed = Array.isArray(data) ? data : [];
      setPatterns(parsed as unknown as LearningPattern[]);
    } finally {
      setIsLoadingPatterns(false);
    }
  }, []);

  /**
   * Load signal recurrence data for composite pattern detection (I-A-11).
   */
  const loadRecurrence = useCallback(async (days = 30) => {
    setIsLoadingRecurrence(true);
    try {
      const { data, error } = await supabase.rpc('get_signal_recurrence', {
        _days: days,
      } as any);

      if (error) {
        console.error('[useNetworkLearning] loadRecurrence error:', error);
        return;
      }

      const parsed = Array.isArray(data) ? data : [];
      setRecurrence(parsed as unknown as RecurrenceEntry[]);
    } finally {
      setIsLoadingRecurrence(false);
    }
  }, []);

  /**
   * Load outcome patterns for the Pensieve Outcome Layer (I-A-14).
   */
  const loadOutcomes = useCallback(async (days = 30) => {
    setIsLoadingOutcomes(true);
    try {
      const { data, error } = await supabase.rpc('get_outcome_patterns', {
        _days: days,
      } as any);

      if (error) {
        console.error('[useNetworkLearning] loadOutcomes error:', error);
        return;
      }

      const raw = Array.isArray(data) ? data : [];
      setOutcomes(classifyOutcomes(raw as unknown as RawOutcomeRow[]));
    } finally {
      setIsLoadingOutcomes(false);
    }
  }, []);

  return {
    recordEvent,
    summary, isLoadingSummary, loadSummary,
    patterns, isLoadingPatterns, loadPatterns,
    recurrence, isLoadingRecurrence, loadRecurrence,
    outcomes, isLoadingOutcomes, loadOutcomes,
  };
}